/**
 * Google OAuth Service for Sign-In with Google
 * Uses OAuth 2.0 Authorization Code Flow with authentication-framework backend
 */
import config from '../config';

// Auth API endpoint - authentication-framework service on port 8084
const AUTH_API_BASE_URL = (() => {
  const base = config.VITE_AUTH_API_BASE_URL;
  if (!base) {
    const coreBase = config.VITE_CORE_API_BASE_URL || config.VITE_API_BASE_URL;
    return coreBase?.replace(/\/$/, '') ;
  }
  return base.replace(/\/$/, '');
})();

const GOOGLE_CLIENT_ID = config.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = config.VITE_GOOGLE_REDIRECT_URI ;

export interface GoogleAuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userId: string;        // User ID from backend
  email: string;         // User email from backend
  userName: string;      // User's full name for display
  tenantId: string;      // Organization/Tenant ID for API calls
  tenantName: string;    // Organization name for display
  role: string;          // User role
  profile?: string;      // User profile from backend
  isNewUser?: boolean;   // Flag for new OAuth registrations
}

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  tenantName: string;    // Organization name for display
  role: string;
  profile?: string;
  picture?: string;
}

/**
 * Initiate Google OAuth2 authorization flow
 * Redirects user to Google's authorization page
 */
export const initiateGoogleLogin = async (): Promise<void> => {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google Client ID not configured');
  }

  try {
    const response = await fetch(
      `${AUTH_API_BASE_URL}/oauth/google?redirectUri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      throw new Error('Failed to get authorization URL');
    }

    const data = await response.json();
    
    // Redirect to Google's authorization page
    window.location.href = data.authorizationUrl;
  } catch (error) {
    console.error('Failed to initiate Google login:', error);
    throw error;
  }
};

/**
 * Handle OAuth callback - exchange authorization code for tokens
 */
export const handleOAuthCallback = async (
  code: string,
  state: string
): Promise<{ authResponse: GoogleAuthResponse; user: GoogleUser }> => {
  const response = await fetch(`${AUTH_API_BASE_URL}/oauth/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      state,
      redirectUri: GOOGLE_REDIRECT_URI,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Google authentication failed');
  }

  const data: GoogleAuthResponse = await response.json();

  console.log('🔐 OAuth callback response:', {
    userId: data.userId,
    email: data.email,
    userName: data.userName,
    tenantId: data.tenantId,
    tenantName: data.tenantName,
    role: data.role,
    isNewUser: data.isNewUser
  });

  const user: GoogleUser = {
    id: data.userId,
    email: data.email,
    name: data.userName || data.tenantName || data.email.split('@')[0],
    tenantId: data.tenantId,
    tenantName: data.tenantName || 'Organization',
    role: data.role,
    profile: data.profile,
  };

  return { authResponse: data, user };
};

/**
 * Legacy function - no longer needed with OAuth2 flow
 * Kept for backward compatibility
 */
export const initializeGoogleAuth = (): Promise<void> => {
  return Promise.resolve();
};

/**
 * Legacy function - no longer needed with OAuth2 flow
 * Kept for backward compatibility
 */
export const renderGoogleButton = (
  elementId: string,
  onSuccess: (response: GoogleAuthResponse, user: GoogleUser) => void,
  onError: (error: string) => void
): void => {
  // No-op: OAuth2 flow uses redirect, not button render
  console.warn('renderGoogleButton is deprecated with OAuth2 flow');
};

/**
 * Legacy function - replaced by handleOAuthCallback
 */
export const handleGoogleCredential = async (
  credential: string
): Promise<{ authResponse: GoogleAuthResponse; user: GoogleUser }> => {
  throw new Error('handleGoogleCredential is not supported with OAuth2 flow');
};

/**
 * Sign out from Google
 */
export const signOutGoogle = (): void => {
  // OAuth2 flow doesn't require special sign-out handling
  // Token revocation would be handled by backend if needed
};

/**
 * Check if Google Auth is available (client ID configured)
 */
export const isGoogleAuthAvailable = (): boolean => {
  return !!GOOGLE_CLIENT_ID;
};

// Remove Google Identity Services type declarations as we're using OAuth2 redirect flow
